import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { techBand, collectAllResponses } from './scoring.js';

const PIN_STORAGE_KEY = 'via-admin-pin';

function readStoredPin() {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(PIN_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function writeStoredPin(pin) {
  if (typeof window === 'undefined') return;
  try {
    if (pin) window.localStorage.setItem(PIN_STORAGE_KEY, pin);
    else window.localStorage.removeItem(PIN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * SubmissionsView
 *
 * Hiring-authority view of all assessment submissions. The view is PIN-gated:
 * on first visit the owner enters a PIN, which is saved to localStorage so
 * future visits skip the prompt. The PIN itself is whatever string the
 * backend's /api/admin/submissions endpoint accepts via ?token=...
 *
 * Auth precedence:
 *   1. adminToken prop (passed via ?token= in URL — kept for dev convenience)
 *   2. localStorage 'via-admin-pin'
 *   3. PIN entry form
 *
 * Wrong PIN → server returns 401 → stored PIN is cleared → form re-appears.
 */
export default function SubmissionsView({ instrument, adminToken: initialToken, adminEndpoint = '/api/admin/submissions' }) {
  const [token, setToken] = useState(initialToken || readStoredPin());
  const [loading, setLoading] = useState(Boolean(initialToken || readStoredPin()));
  const [error, setError] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [filterFit, setFilterFit] = useState('all');
  const [sortBy, setSortBy] = useState('startedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [pinInput, setPinInput] = useState('');
  const [expanded, setExpanded] = useState(null); // sessionId of the open detail row
  const [confirmingDelete, setConfirmingDelete] = useState(null); // sessionId pending confirm
  const [deletingId, setDeletingId] = useState(null); // sessionId mid-delete

  // Expanding a submission row reveals the candidate's full answer transcript
  // — every question, grouped by section, in instrument order. The backend
  // returns each submission's raw `responses` map unfiltered; `collectAll
  // Responses` (per row, below) pairs it with the question text and option
  // labels. `hasDetail` drives whether the table carries an expand column at
  // all — true whenever the instrument has any questions to show.
  const hasDetail = useMemo(
    () => (instrument?.groups || []).some((g) => (g.questions || []).length > 0),
    [instrument]
  );
  const toggleRow = (id) => setExpanded((cur) => (cur === id ? null : id));

  // Map a stored quadrant id (e.g. "vintage-specialist") to its human label
  // (e.g. "The Hollywood Mechanic") for display. The id is the stable, stored
  // value; the label is what candidates see. Falls back to the raw id for any
  // legacy/unknown id or if no instrument is provided.
  const quadrantLabel = useMemo(() => {
    const m = {};
    for (const q of instrument?.quadrants || []) m[q.id] = q.label;
    if (instrument?.indeterminate) m[instrument.indeterminate.id] = instrument.indeterminate.label;
    return (id) => m[id] || id;
  }, [instrument]);

  const loadSubmissions = useCallback((t) => {
    setLoading(true);
    setError(null);
    const sep = adminEndpoint.includes('?') ? '&' : '?';
    fetch(`${adminEndpoint}${sep}token=${encodeURIComponent(t)}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (r.status === 401) {
          writeStoredPin('');
          setToken('');
          setError("That PIN didn't work. Try again.");
        } else if (!data.ok) {
          setError(data.message || 'Failed to load submissions.');
        } else {
          setSubmissions(data.submissions || []);
        }
        setLoading(false);
      })
      .catch((e) => {
        setError(String(e));
        setLoading(false);
      });
  }, [adminEndpoint]);

  useEffect(() => {
    if (token) loadSubmissions(token);
  }, [token, loadSubmissions]);

  const submitPin = (e) => {
    e.preventDefault();
    const trimmed = pinInput.trim();
    if (!trimmed) return;
    writeStoredPin(trimmed);
    setToken(trimmed);
    setPinInput('');
  };

  const signOut = () => {
    writeStoredPin('');
    setToken('');
    setSubmissions([]);
    setError(null);
  };

  // Permanently delete one entry (spam, noise, or a test run). The backend
  // deletes the lead row, which cascades to its submission, so the row leaves
  // the table entirely. Hard delete — irreversible, hence the inline confirm.
  // The PIN (token) authorizes it, same as loading.
  const deleteSubmission = useCallback(
    (sessionId) => {
      setDeletingId(sessionId);
      setError(null);
      const sep = adminEndpoint.includes('?') ? '&' : '?';
      fetch(
        `${adminEndpoint}${sep}token=${encodeURIComponent(token)}&sessionId=${encodeURIComponent(sessionId)}`,
        { method: 'DELETE' }
      )
        .then(async (r) => {
          const data = await r.json().catch(() => ({}));
          if (!r.ok || !data.ok) {
            setError(data.message || 'Could not delete that entry.');
          } else {
            setSubmissions((rows) => rows.filter((s) => s.sessionId !== sessionId));
            setExpanded((cur) => (cur === sessionId ? null : cur));
          }
          setDeletingId(null);
          setConfirmingDelete(null);
        })
        .catch((e) => {
          setError(String(e));
          setDeletingId(null);
          setConfirmingDelete(null);
        });
    },
    [adminEndpoint, token]
  );

  const byEmail = useMemo(() => {
    const map = new Map();
    for (const s of submissions) {
      if (!map.has(s.email)) map.set(s.email, []);
      map.get(s.email).push(s);
    }
    return map;
  }, [submissions]);

  const fitOf = (s) => {
    const fitMap = {
      'vintage-specialist': 'ideal',
      'master-technician': 'secondary',
      tinkerer: 'weak',
      'service-mechanic': 'exclude',
      indeterminate: 'indeterminate',
    };
    return fitMap[s.quadrantId] || '—';
  };

  const filtered = useMemo(() => {
    let rows = submissions.filter((s) => {
      if (filterFit === 'all') return true;
      if (filterFit === 'incomplete') return !s.completedAt;
      if (filterFit === 'tech-strong') return s.technicalScore && s.technicalScore.percent >= 75;
      if (filterFit === 'tech-weak')   return s.technicalScore && s.technicalScore.percent < 50;
      return fitOf(s) === filterFit;
    });
    rows = [...rows].sort((a, b) => {
      const av = a[sortBy] || '';
      const bv = b[sortBy] || '';
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [submissions, filterFit, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  const exportCsv = () => {
    const cols = [
      'email', 'name', 'phone', 'experienceYears', 'attemptNumber',
      'quadrantId', 'startedAt', 'completedAt',
      'curiosityScore', 'masteryScore',
      'technicalCorrect', 'technicalTotal', 'technicalPercent',
    ];
    const rows = filtered.map((s) => [
      s.email,
      s.name,
      s.phone,
      s.experienceYears,
      s.attemptNumber,
      s.quadrantId || '',
      s.startedAt,
      s.completedAt || '',
      s.axisScores?.curiosity ?? '',
      s.axisScores?.mastery ?? '',
      s.technicalScore?.correct ?? '',
      s.technicalScore?.total ?? '',
      s.technicalScore?.percent ?? '',
    ]);
    const csv = [cols.join(','), ...rows.map((r) =>
      r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')
    )].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // PIN gate — shown when no valid token is known.
  if (!token) {
    return (
      <section className="via-card via-pin-gate">
        <div className="via-eyebrow">Hiring authority view</div>
        <h1 className="via-title">Submissions admin</h1>
        <p className="via-subtitle">
          Enter the access PIN to see candidate submissions. The PIN is set by whoever
          deployed this site — ask them if you don't have it.
        </p>
        <form onSubmit={submitPin} className="via-pin-form">
          <input
            type="password"
            autoComplete="current-password"
            placeholder="Access PIN"
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value)}
            className="via-pin-input"
            autoFocus
          />
          <button type="submit" className="via-btn via-btn-primary">Sign in</button>
        </form>
        {error && <p className="via-pin-error">{error}</p>}
      </section>
    );
  }

  if (loading) return <div className="via-card via-loading">Loading submissions…</div>;
  if (error) {
    return (
      <div className="via-card via-error">
        <h2>Couldn't load submissions</h2>
        <p>{error}</p>
        <button className="via-btn" onClick={signOut} style={{ marginTop: 12 }}>
          Sign out and re-enter PIN
        </button>
      </div>
    );
  }

  return (
    <section className="via-card via-submissions">
      <div className="via-eyebrow">Hiring authority view</div>
      <h1 className="via-title">Submissions</h1>
      <p className="via-subtitle">
        {submissions.length} total · {byEmail.size} unique candidates · {' '}
        {submissions.filter((s) => s.completedAt).length} completed
      </p>

      <div className="via-submissions-controls">
        <label>
          Filter:&nbsp;
          <select value={filterFit} onChange={(e) => setFilterFit(e.target.value)}>
            <option value="all">All</option>
            <option value="ideal">Ideal fit (Hollywood Mechanic)</option>
            <option value="secondary">Secondary (Master Technician)</option>
            <option value="weak">Weak (Tinkerer)</option>
            <option value="exclude">Exclude (Service Mechanic)</option>
            <option value="indeterminate">Mixed Profile (indeterminate)</option>
            <option value="tech-strong">High technical (≥ 75%)</option>
            <option value="tech-weak">Low technical (&lt; 50%)</option>
            <option value="incomplete">Incomplete (abandoned)</option>
          </select>
        </label>
        <button className="via-btn" onClick={exportCsv}>Export CSV</button>
        <button className="via-btn" onClick={signOut}>Sign out</button>
      </div>

      <div className="via-table-wrap">
        <table className="via-table">
          <thead>
            <tr>
              {hasDetail && <th aria-label="Expand row" />}
              <th onClick={() => toggleSort('email')}>Email ↕</th>
              <th onClick={() => toggleSort('name')}>Name ↕</th>
              <th onClick={() => toggleSort('attemptNumber')}>Attempt ↕</th>
              <th onClick={() => toggleSort('quadrantId')}>Quadrant ↕</th>
              <th>Technical</th>
              <th>Curiosity</th>
              <th>Mastery</th>
              <th onClick={() => toggleSort('startedAt')}>Started ↕</th>
              <th onClick={() => toggleSort('completedAt')}>Completed ↕</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => {
              const fit = fitOf(s);
              const isOpen = expanded === s.sessionId;
              // A row expands only if it has recorded answers — incomplete
              // (lead-only) rows have null/empty responses and stay static.
              const hasAnswers = s.responses && Object.keys(s.responses).length > 0;
              const expandable = hasDetail && hasAnswers;
              return (
                <React.Fragment key={s.sessionId}>
                <tr
                  className={`via-fit-row-${fit}${expandable ? ' via-row-clickable' : ''}`}
                  onClick={expandable ? () => toggleRow(s.sessionId) : undefined}
                >
                  {hasDetail && (
                    <td className="via-row-caret" aria-hidden="true">{expandable ? (isOpen ? '▾' : '▸') : ''}</td>
                  )}
                  <td><strong>{s.email}</strong></td>
                  <td>{s.name}</td>
                  <td>{s.attemptNumber > 1 ? <span className="via-attempt-flag">{s.attemptNumber}</span> : s.attemptNumber}</td>
                  <td>
                    {s.quadrantId ? (
                      <span className={`via-quad-pill via-fit-${fit}`} title={s.quadrantId}>{quadrantLabel(s.quadrantId)}</span>
                    ) : (
                      <span className="via-incomplete">incomplete</span>
                    )}
                  </td>
                  <td>
                    {s.technicalScore && s.technicalScore.total > 0 ? (
                      <span className={`via-tech-pill via-tech-${techBand(s.technicalScore.percent)}`}>
                        {s.technicalScore.correct}/{s.technicalScore.total}
                      </span>
                    ) : (
                      <span className="via-incomplete">—</span>
                    )}
                  </td>
                  <td>{s.axisScores ? s.axisScores.curiosity.toFixed(2) : '—'}</td>
                  <td>{s.axisScores ? s.axisScores.mastery.toFixed(2) : '—'}</td>
                  <td>{formatDt(s.startedAt)}</td>
                  <td>{s.completedAt ? formatDt(s.completedAt) : '—'}</td>
                  <td className="via-row-actions" onClick={(e) => e.stopPropagation()}>
                    {confirmingDelete === s.sessionId ? (
                      <span className="via-delete-confirm">
                        <button
                          className="via-delete-yes"
                          disabled={deletingId === s.sessionId}
                          onClick={() => deleteSubmission(s.sessionId)}
                        >
                          {deletingId === s.sessionId ? 'Deleting…' : 'Delete'}
                        </button>
                        <button
                          className="via-delete-no"
                          disabled={deletingId === s.sessionId}
                          onClick={() => setConfirmingDelete(null)}
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        className="via-delete-btn"
                        title="Delete this entry"
                        aria-label={`Delete entry for ${s.email}`}
                        onClick={() => setConfirmingDelete(s.sessionId)}
                      >
                        🗑
                      </button>
                    )}
                  </td>
                </tr>
                {expandable && isOpen && (
                  <tr className="via-row-detail">
                    <td colSpan={hasDetail ? 11 : 10}>
                      <div className="via-detail-wrap">
                        <div className="via-detail-title">
                          All answers — {s.name || s.email}
                        </div>
                        {collectAllResponses(instrument, s.responses || {}).map((group) => (
                          <div key={group.groupId} className="via-detail-group">
                            <div className="via-detail-group-title">{group.groupTitle}</div>
                            {group.items.map((item) => (
                              <div key={item.questionId} className="via-detail-qa">
                                <div className="via-detail-q">{item.stem}</div>
                                <TranscriptAnswer item={item} />
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={hasDetail ? 11 : 10} style={{ textAlign: 'center', color: 'var(--via-muted)', padding: 24 }}>
                  No submissions match the current filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// Render one transcript row's answer, switching on question type. Disposition
// items show the chosen label + an [axis ±score] tag; technical items show the
// choice + ✓/✗ (and the correct answer when wrong) with any rationale beneath;
// open-response shows the typed text.
function TranscriptAnswer({ item }) {
  if (item.type === 'open-response') {
    const text = (item.openText || '').trim();
    return (
      <div className={`via-detail-a${text ? '' : ' via-detail-a-empty'}`}>
        {text || '— (skipped)'}
      </div>
    );
  }

  if (item.type === 'technical-mcq') {
    return (
      <>
        <div className="via-detail-chosen">
          {item.answered ? (
            <>
              {item.chosenLabel}{' '}
              {item.isCorrect ? (
                <span className="via-detail-correct">✓ correct</span>
              ) : (
                <span className="via-detail-incorrect">
                  ✗{item.correctLabel ? ` (correct: ${item.correctLabel})` : ''}
                </span>
              )}
            </>
          ) : (
            <span className="via-detail-a-empty">— (skipped)</span>
          )}
        </div>
        {item.rationale != null && (
          <div className={`via-detail-a${item.rationale.trim() ? '' : ' via-detail-a-empty'}`}>
            {item.rationale.trim() || '— (no rationale given)'}
          </div>
        )}
      </>
    );
  }

  // disposition (likert-4 / forced-choice)
  return (
    <div className="via-detail-chosen">
      {item.answered ? (
        <>
          {item.chosenLabel}
          {item.axisLabel && (
            <span className="via-detail-tag">
              {item.axisLabel} {item.score > 0 ? '+' : ''}{item.score}
            </span>
          )}
        </>
      ) : (
        <span className="via-detail-a-empty">— (skipped)</span>
      )}
    </div>
  );
}

function formatDt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString();
}
